import { useEffect, useRef, useState } from 'react'
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';
import styles from './AppContainer.module.scss';
import Login from '../../auth/login/Login';
import Register from '../../auth/register/Register';
import ProductsPage from '../../components/products-page/ProductsPage';
import CmsContainer from '../cms-container/CmsContainer';
import Header from '../../shared/header/Header';
import Cart from '../../components/cart/Cart';
import ProductDetailsView from '../../components/product-details-view/ProductDetailsView';
import ProducerDetailsView from '../../components/producer-details-view/ProducerDetailsView';
import Order from '../../components/order/Order';
import OrderConfirmation from '../../components/order-confirmation/OrderConfirmation';
import ProducersPage from '../../components/producers-page/ProducersPage';
import ClientPanel from '../../components/client-panel/ClientPanel';
import OrdersHistory from '../../components/client-panel/components/orders-history/OrdersHistory';
import AddressDetails from '../../components/client-panel/components/address-details/AddressDetails';
import MyAccountInfo from '../../components/client-panel/components/my-account-info/MyAccounInfo';
import UserDetails from '../../components/client-panel/components/user-details/UserDetails';
import OrderDetails from '../../components/client-panel/components/order-details/OrderDetails';
import SellingSessions from '../../components/selling-sessions/SellingSessions';
import { usePluginStore } from 'react-pluggable';
import PickupMethods from '../../components/pickup-methods/PickupMethods';
import {
    ABOUT, CONTACT, HOW_TO_BUY, JOIN_US, PRIVACY_POLICY, PROJECT, PRZELEWY24_OBLIGATIONS,
    PRZELEWY24_REGULATIONS, REGULATIONS,
    WEBSITE_REGULATIONS
} from '../../shared/constants/cms-codes.constants';
import { getCmsUrlAndTitleByLanguagAndCode, getAllAvailableCmsPages } from '../cms-container/services';
import OrderTransactionConfirmation from '../../components/order-transaction-confirmation/OrderTransactionConfirmation';
import Przelewy24PaymentPage from '../../components/przelewy24-payment-page/Przelewy24PaymentPage';
import { CmsTitlesAndUrls } from '../cms-container/models/cms-titles-url-by-code';
import ContinueOrCreateAccount from '../continue-or-create-account/ContinueOrCreateAccount';
import ResetPasswordContainer from '../reset-password-container/ResetPaswordContainer';

export default function AppContainer() {
    const pluginStore: any = usePluginStore();
    let Renderer = pluginStore.executeFunction("Renderer.getRendererComponent");
    const [cartItemsChanged, setCartItemsChanged] = useState<boolean>(false);
    const [titlesAndUrls, setTitlesAndUrls] = useState<CmsTitlesAndUrls>({});
    const [allCmsPages, setAllCmsPages] = useState<{ [key: string]: any }>({});
    const [loading, setLoading] = useState(true);
    const cmsContainerRef = useRef<HTMLElement | null>(null);
    const location = useLocation();

    const [selectedCategory, setSelectedCategory] = useState<string>();
    const [isSearched, setIsSearched] = useState<boolean>(false);

    const handleCategorySelect = (categoryId: string): void => {
        setSelectedCategory(categoryId);
        localStorage.setItem('categoryId', categoryId);
        setIsSearched(true);
    };

    const updateIsSearched = (value: boolean): void => {
        setIsSearched(value);
    }


    const getTitleAndUrlForHeader = async (): Promise<void[]> => {
        const savedLanguageCode = localStorage.getItem('lang');
        if (savedLanguageCode) {
            try {
                const cmsListResponse = await getAllAvailableCmsPages();
                
                if (cmsListResponse && cmsListResponse.data && Array.isArray(cmsListResponse.data)) {
                    const availableCmsPages = cmsListResponse.data;
                    
                    const dynamicCodes = availableCmsPages
                        .filter((page: any) => page.code)
                        .map((page: any) => page.code);
                    
                    const results = await Promise.allSettled(
                        dynamicCodes.map(async (code: string) => {
                            const response = await getCmsUrlAndTitleByLanguagAndCode(code, savedLanguageCode);
                            if (response && response.data && response.data.url) {
                                return { code, data: response.data };
                            }
                            throw new Error(`Code ${code} not found`);
                        })
                    );
                    
                    const foundCodes: string[] = [];
                    results.forEach((result) => {
                        if (result.status === 'fulfilled') {
                            const { code, data } = result.value;
                            foundCodes.push(code);
                            setTitlesAndUrls((prevTitlesAndUrls) => ({
                                ...prevTitlesAndUrls,
                                [code]: data,
                            }));
                            
                            setAllCmsPages((prevAllCmsPages) => ({
                                ...prevAllCmsPages,
                                [code]: data,
                            }));
                        }
                    });
                    
                } else {
                }
                
            } catch (error) {
                console.error('Błąd podczas pobierania stron CMS z ERP:', error);
                
                const fallbackCodes = [
                    HOW_TO_BUY, CONTACT, JOIN_US, ABOUT, WEBSITE_REGULATIONS, 
                    PRIVACY_POLICY, PROJECT, PRZELEWY24_OBLIGATIONS, PRZELEWY24_REGULATIONS, REGULATIONS
                ];
                
                const results = await Promise.allSettled(
                    fallbackCodes.map(async (code) => {
                        const response = await getCmsUrlAndTitleByLanguagAndCode(code, savedLanguageCode);
                        if (response && response.data && response.data.url) {
                            return { code, data: response.data };
                        }
                        throw new Error(`Code ${code} not found`);
                    })
                );
                
                results.forEach((result) => {
                    if (result.status === 'fulfilled') {
                        const { code, data } = result.value;
                        setTitlesAndUrls((prevTitlesAndUrls) => ({
                            ...prevTitlesAndUrls,
                            [code]: data,
                        }));
                        
                        setAllCmsPages((prevAllCmsPages) => ({
                            ...prevAllCmsPages,
                            [code]: data,
                        }));
                    }
                });
            }
            setLoading(false);
        }

        return Promise.resolve([]);
    };


    function handleCartItemsChange(): void {
        setCartItemsChanged(!cartItemsChanged);
    }

    function handleRemoveCartItem(): void {
        setCartItemsChanged(!cartItemsChanged);
    }


    const [currentLanguage, setCurrentLanguage] = useState<string | null>(localStorage.getItem('lang'));

    useEffect(() => {
        getTitleAndUrlForHeader();
        
        const handleLanguageChange = () => {
            getTitleAndUrlForHeader();
        };
        
        window.addEventListener('languageChanged', handleLanguageChange);
        
        return () => {
            window.removeEventListener('languageChanged', handleLanguageChange);
        };
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            const newLang = localStorage.getItem('lang');
            if (newLang !== currentLanguage) {
                setCurrentLanguage(newLang);
                getTitleAndUrlForHeader();
            }
        }, 1000);
        
        return () => clearInterval(interval);
    }, [currentLanguage]);

    useEffect(() => {
    }, [loading, titlesAndUrls]);

    const scrollToCmsContainer = () => {
        if (cmsContainerRef.current) {
            window.scrollTo({
                top: cmsContainerRef.current.offsetTop,
                behavior: 'smooth',
            });
        }
    };

    useEffect(() => {
        scrollToCmsContainer();
    }, [location.pathname]);



    return (
        <div className={styles.main_container}>
            <div className={styles.header}>
                <Header
                    selectedCategory={selectedCategory}
                    isSearched={isSearched}
                    updateIsSearched={updateIsSearched}
                    handleCategorySelect={handleCategorySelect}
                    cartItemsChanged={cartItemsChanged} />
            </div>
            <div className={styles.content}>
                {loading ? (
                    <div></div>
                ) : (
                    <Routes>
                        {Object.entries(allCmsPages).map(([code, pageData]) => (
                            pageData?.url && (
                                <Route 
                                    key={code}
                                    path={`/:language?${pageData.url}`} 
                                    element={<CmsContainer code={code} />} 
                                />
                            )
                        ))}
                        
                        {Object.entries(titlesAndUrls).map(([code, pageData]) => (
                            pageData?.url && !allCmsPages[code] && (
                                <Route 
                                    key={`fallback-${code}`}
                                    path={`/:language?${pageData.url}`} 
                                    element={<CmsContainer code={code} />} 
                                />
                            )
                        ))}

                        <Route path='/:language?/catalogue/:categoryId'
                            element={
                                <ProductsPage
                                    selectedCategory={selectedCategory}
                                    isSearched={isSearched}
                                    updateIsSearched={updateIsSearched}
                                    handleCategorySelect={handleCategorySelect}
                                    onCartItemsChange={handleCartItemsChange} />
                            } />
                        <Route path='/:language?/login' Component={Login} />
                        <Route path='/:language?/register' Component={Register} />
                        <Route path='/:language?/client-panel' Component={ClientPanel} >
                            <Route path='' element={<MyAccountInfo />} />
                            <Route path='orders' element={<OrdersHistory />} />
                            <Route path='orders/:orderId' element={<OrderDetails />} />
                            <Route path='address' element={<AddressDetails />} />
                            <Route path='user-details' element={<UserDetails />} />
                        </Route>
                        <Route path='/:language?/cart' element={<Cart onCartItemRemoved={handleRemoveCartItem} onCartItemsChange={handleCartItemsChange} />} />
                        <Route path='/:language?/product-details/:productId/:categoryId/:producerId?' element={<ProductDetailsView onCartItemsChange={handleCartItemsChange} />} />
                        <Route path='/:language?/producer-details-view/:producerId' element={<ProducerDetailsView onCartItemsChange={handleCartItemsChange} />} />
                        <Route path='/:language?/order' element={<Order onCartItemRemoved={handleRemoveCartItem} onCartItemsChange={handleCartItemsChange} />} />
                        <Route path='/:language?/order-confirmation/:orderId' element={<OrderConfirmation />} />
                        <Route path='/:language?/register-transaction-confirmation' element={<OrderTransactionConfirmation />} />
                        <Route path='/:language?/producers' element={<ProducersPage />} />
                        <Route path='/:language?/selling-sessions' element={<SellingSessions />} />
                        <Route path='/:language?/pickup-methods' element={<PickupMethods />} />
                        <Route path='/:language?/continue-or-create-account' element={<ContinueOrCreateAccount />} />
                        <Route path='/:language?/przelewy24payment/:orderId' element={<Przelewy24PaymentPage />} />
                        <Route path="/:language?/reset-password/*" Component={ResetPasswordContainer}></Route>
                        <Route path='/:language?'
                            element={
                                <ProductsPage
                                    selectedCategory={selectedCategory}
                                    isSearched={isSearched}
                                    updateIsSearched={updateIsSearched}
                                    handleCategorySelect={handleCategorySelect}
                                    onCartItemsChange={handleCartItemsChange} />
                            } />
                        <Route path="*" element={<Navigate to="/" />} />
                    </Routes>
                )}
            </div >
            <div className={styles.footer}>
                <Renderer placement="page:footer" />
            </div>
        </div >
    );
}
